/**
 * DevToolPanel Hook
 * Main coordinator for the dev tool panel
 * 
 * Responsibilities:
 * - Coordinate between ResizeHandle and KeyboardShortcuts
 * - Manage panel state and transitions
 * - Integrate with LocalStorage for persistence
 * - Handle communication with LiveView
 */

const DevToolPanel = {
  mounted() {
    // Panel state
    this.panelState = 'collapsed';
    this.panelHeight = 400;
    this.currentTab = 'info';
    
    // Configuration
    this.storageKey = 'jump-dev-tool-state';
    this.minHeight = 100;
    this.maxHeight = window.innerHeight * 0.9;
    
    // Bind methods
    this.handleResize = this.handleResize.bind(this);
    this.handleKeyboardShortcut = this.handleKeyboardShortcut.bind(this);
    this.saveState = this.saveState.bind(this);
    this.loadState = this.loadState.bind(this);
    this.updatePanelClasses = this.updatePanelClasses.bind(this);
    
    // Load saved state
    this.loadState();
    
    // Listen for events from child hooks
    this.el.addEventListener('panel-resized', this.handleResize);
    this.el.addEventListener('keyboard-shortcut', this.handleKeyboardShortcut);
    
    // Listen for window resize to adjust max height
    this.handleWindowResize = () => {
      this.maxHeight = window.innerHeight * 0.9;
      if (this.panelHeight > this.maxHeight) {
        this.panelHeight = this.maxHeight;
        this.el.style.height = `${this.panelHeight}px`;
        this.saveState();
      }
    };
    window.addEventListener('resize', this.handleWindowResize);
    
    // Apply initial state
    this.updatePanelClasses();
    if (this.panelState === 'open') {
      this.el.style.height = `${this.panelHeight}px`;
    }
    
    // Listen for LiveView events
    this.handleEvent('panel-state-changed', ({ state, height }) => {
      this.panelState = state;
      if (height) {
        this.panelHeight = height;
      }
      this.updatePanelClasses();
      this.saveState();
    });
    
    this.handleEvent('tab-changed', ({ tab }) => {
      this.currentTab = tab;
      this.saveState();
    });
  },
  
  handleResize(e) {
    const { height } = e.detail;
    this.panelHeight = Math.max(this.minHeight, Math.min(height, this.maxHeight));
    this.el.style.height = `${this.panelHeight}px`;
    
    // Update CSS variable for live debugger positioning
    document.documentElement.style.setProperty('--dev-panel-height', `${this.panelHeight}px`);
    
    // Push height to LiveView
    this.pushEvent('resize_panel', { height: this.panelHeight });
    
    // Save state
    this.saveState();
  },
  
  handleKeyboardShortcut(e) {
    const { action } = e.detail;
    
    switch (action) {
      case 'toggle-panel':
        this.togglePanel();
        break;
      case 'close-panel':
        this.closePanel();
        break;
      case 'focus-panel':
        this.focusPanel();
        break;
      case 'next-tab':
      case 'prev-tab':
        // These are handled by LiveView
        break;
    }
  },
  
  togglePanel() {
    if (this.panelState === 'collapsed') {
      this.panelState = 'open';
      this.pushEvent('open_panel', {});
    } else {
      this.panelState = 'collapsed';
      this.pushEvent('close_panel', {});
    }
    
    this.updatePanelClasses();
    this.saveState();
  },
  
  closePanel() {
    if (this.panelState !== 'collapsed') {
      this.panelState = 'collapsed';
      this.pushEvent('close_panel', {});
      this.updatePanelClasses();
      this.saveState();
    }
  },
  
  focusPanel() {
    if (this.panelState === 'collapsed') {
      this.panelState = 'open';
      this.pushEvent('open_panel', {});
      this.updatePanelClasses();
      this.saveState();
    }
    
    // Focus the first focusable element in the panel
    setTimeout(() => {
      const focusable = this.el.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable) {
        focusable.focus();
      }
    }, 100);
  },
  
  updatePanelClasses() {
    // Remove all state classes
    this.el.classList.remove('translate-y-full', 'translate-y-[calc(100%-50px)]', 'translate-y-0');
    
    // Add appropriate class based on state
    switch (this.panelState) {
      case 'collapsed':
        this.el.classList.add('translate-y-full');
        document.documentElement.style.removeProperty('--dev-panel-height');
        break;
      case 'minimized':
        this.el.classList.add('translate-y-[calc(100%-50px)]');
        document.documentElement.style.setProperty('--dev-panel-height', '50px');
        break;
      case 'open':
        this.el.classList.add('translate-y-0');
        this.el.style.height = `${this.panelHeight}px`;
        document.documentElement.style.setProperty('--dev-panel-height', `${this.panelHeight}px`);
        break;
    }
  },
  
  saveState() {
    const state = {
      panelState: this.panelState,
      panelHeight: this.panelHeight,
      currentTab: this.currentTab,
      timestamp: Date.now()
    };
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save dev tool state:', e);
    }
  },
  
  loadState() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const state = JSON.parse(saved);
        
        // Restore state if it's less than 24 hours old
        if (state.timestamp && Date.now() - state.timestamp < 24 * 60 * 60 * 1000) {
          this.panelState = state.panelState || 'collapsed';
          this.panelHeight = state.panelHeight || 400;
          this.currentTab = state.currentTab || 'info';
          
          // Ensure height is within bounds
          this.panelHeight = Math.max(this.minHeight, Math.min(this.panelHeight, this.maxHeight));
          
          // Push restored state to LiveView
          this.pushEventTo(this.el, 'restore_state', {
            panel_state: this.panelState,
            panel_height: this.panelHeight,
            current_tab: this.currentTab
          });
        }
      }
    } catch (e) {
      console.error('Failed to load dev tool state:', e);
    }
  },
  
  destroyed() {
    // Clean up event listeners
    this.el.removeEventListener('panel-resized', this.handleResize);
    this.el.removeEventListener('keyboard-shortcut', this.handleKeyboardShortcut);
    window.removeEventListener('resize', this.handleWindowResize);
    
    // Save final state
    this.saveState();
  }
};

export default DevToolPanel;